import React, { useEffect, useState } from "react";
import {
    Box, CircularProgress, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Typography,
} from "@mui/material";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";
import { buildCustomerPath, loyaltyBrandHeaders } from "../../utils/loyaltyCustomerApi";
import {
    historyEventToLastChange,
    lastChangeLabel,
    normalizeLoyaltyHistory,
} from "../../utils/loyaltyQualification";
import LastChangeChip from "./LastChangeChip";

function formatWhen(value) {
    return value ? new Date(value).toLocaleString("fr-FR") : "–";
}

export default function LoyaltyHistoryPanel({ brandName, profileId, email = "", refreshToken = 0 }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!brandName || !profileId) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);
        loyaltyFetch(buildCustomerPath(brandName, profileId, "/loyalty/history", email), {
            headers: loyaltyBrandHeaders(brandName),
        })
            .then(async (res) => {
                if (!res.ok) throw new Error(`Erreur HTTP ${res.status}`);
                const data = await res.json();
                if (!cancelled) setRows(normalizeLoyaltyHistory(data));
            })
            .catch((err) => { if (!cancelled) setError(err.message); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [brandName, profileId, email, refreshToken]);

    return (
        <Box>
            <Typography variant="h6" fontWeight={700} fontFamily="Montserrat, sans-serif" mb={1.5} color="#1a1a2e" fontSize={15}>
                Historique des paliers
            </Typography>
            {loading ? (
                <Box py={2} display="flex" justifyContent="center"><CircularProgress size={22} /></Box>
            ) : (
                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: "1px solid #e8ecf1" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#f8f9ff" }}>
                                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Événement</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Détail</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={3} align="center" sx={{ py: 3, color: "text.secondary", fontStyle: "italic" }}>
                                        {error || "Aucun événement de palier."}
                                    </TableCell>
                                </TableRow>
                            ) : rows.map((row, idx) => {
                                const change = historyEventToLastChange(row);
                                const fromTier = row.from_tier || row.fromTier || row.previous_tier || row.previousTier;
                                const toTier = row.to_tier || row.toTier || row.new_tier || row.newTier;
                                return (
                                    <TableRow key={row.id || `${change}-${idx}`} hover>
                                        <TableCell sx={{ fontSize: 13 }}>
                                            {formatWhen(row.created_at || row.createdAt || row.changed_at || row.timestamp)}
                                        </TableCell>
                                        <TableCell>
                                            <LastChangeChip value={change} />
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 13 }}>
                                            {fromTier || toTier
                                                ? `${fromTier || "–"} → ${toTier || "–"}`
                                                : (row.reason || row.detail || lastChangeLabel(change) || "–")}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}
